import { BusyWatcher, ScreenBase, watchBusy } from "@frui.ts/screens";
import { Modules, SilentErrors } from "config";
import type LocalizationService from "services/localizationService";
import type AccountingSubLedgerReportsRepository from "../../../repositories/accountingSubLedgerReportsRepository";
import { action, computed, observable, runInAction } from "mobx";
import SubLedgerReportRequest, { SubLedgerReportRequestValidationRules } from "../../../entities/subLedgerReportRequest";
import type MonthOptionsService from "../../../services/monthOptionsService";
import {
  SubLedgerReportChargeOrderRequestValidationRules,
  SubLedgerReportChargeOrderRequestX,
} from "../../../entities/subLedgerReportChargeOrderRequest";
import { excludeEmptyValues, isFilterDirty, isFilterEmpty } from "../../../utils/filters";
import SubLedgerReportChargeRequest, {
  SubLedgerReportChargeRequestValidationRules,
  SubLedgerReportChargeRequestX,
} from "../../../entities/subLedgerReportChargeRequest";
import type CountrySelectViewModel from "../../countrySelectViewModel";
import SubLedgerReportTransferPaymentRequest, {
  SubLedgerReportTransferPaymentRequestValidationRules,
} from "../../../entities/subLedgerReportTransferPaymentRequest";
import SubLedgerReportPayGatePaymentRequest, {
  SubLedgerReportPayGatePaymentRequestValidationRules,
} from "../../../entities/subLedgerReportPayGatePaymentRequest";
import SubLedgerReportSummaryAccountingDocumentRequest, {
  SubLedgerReportSummaryAccountingDocumentRequestValidationRules,
} from "../../../entities/subLedgerReportSummaryAccountingDocumentRequest";
import AccountingDocumentSearchPermissibility from "../../../entities/accountingDocumentSearchPermissibility";
import { subLedgerReportGenerated } from "../../../services/eventBus";
import type EventBus from "../../../services/eventBus";
import { scrollTop } from "../../../utils/scrollTop";
import type KioskRepository from "../../../repositories/kioskRepository";
import type { ISelectItem } from "../../../types";
import type DistributorRepository from "../../../repositories/distributorRepository";
import { addError, attachAutomaticValidator, attachManualValidator, removeError, validate } from "@frui.ts/validation";
import type DateRangeRequest from "../../../entities/dateRangeRequest";
import type { PropertyName } from "@frui.ts/helpers";
import type SubLedgerReportPreviewResponse from "../../../entities/subLedgerReportPreviewResponse";

enum SubLedgerReportPossible {
  Possible,
  NotPossible,
  PreviewRequired,
}

export default class SubLedgerNewReportViewModel extends ScreenBase {
  busyWatcher = new BusyWatcher();

  readonly countrySelectViewModel: CountrySelectViewModel;

  @observable filter: SubLedgerReportRequest;
  @observable order: SubLedgerReportChargeOrderRequestX;
  @observable charge: SubLedgerReportChargeRequest;
  @observable transfer: SubLedgerReportTransferPaymentRequest;
  @observable card: SubLedgerReportPayGatePaymentRequest;
  @observable summary: SubLedgerReportSummaryAccountingDocumentRequest;

  @observable showSuccessModal = false;
  @observable previewFailedTooManyRows?: number = undefined;
  @observable showNotFoundModal?: string;

  @observable kiosks: ISelectItem[] = [];
  @observable salespoints: ISelectItem[] = [];

  public onCloseClick?: () => void;

  constructor(
    protected eventBus: EventBus,
    private localizationService: LocalizationService,
    private monthOptionService: MonthOptionsService,
    private repository: AccountingSubLedgerReportsRepository,
    private kioskRepository: KioskRepository,
    private distributorRepository: DistributorRepository,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super();

    this.countrySelectViewModel = this.countrySelectViewModelFactory("id", true);

    this.createRequest();
  }

  protected async onInitialize(): Promise<any> {
    await this.loadKiosks();

    attachAutomaticValidator(this.filter, SubLedgerReportRequestValidationRules);
    attachAutomaticValidator(this.order, SubLedgerReportChargeOrderRequestValidationRules);
    attachAutomaticValidator(this.charge, SubLedgerReportChargeRequestValidationRules);
    attachAutomaticValidator(this.transfer, SubLedgerReportTransferPaymentRequestValidationRules);
    attachAutomaticValidator(this.card, SubLedgerReportPayGatePaymentRequestValidationRules);
    attachAutomaticValidator(this.summary, SubLedgerReportSummaryAccountingDocumentRequestValidationRules);

    return super.onInitialize();
  }

  private createRequest() {
    this.filter = new SubLedgerReportRequest();
    this.order = new SubLedgerReportChargeOrderRequestX();
    this.charge = new SubLedgerReportChargeRequestX();

    // This is hack because AccountNumberOrIbanAndBankCodeInput wouldn’t handle reseting the transfer correctly
    if (!this.transfer) {
      this.transfer = new SubLedgerReportTransferPaymentRequest();
    }
    this.transfer.bankAccount = undefined;
    this.transfer.constantSymbol = undefined;
    this.transfer.specificSymbol = undefined;
    this.transfer.variableSymbol = undefined;

    this.card = new SubLedgerReportPayGatePaymentRequest();
    this.summary = new SubLedgerReportSummaryAccountingDocumentRequest();

    attachAutomaticValidator(this.filter, SubLedgerReportRequestValidationRules);
    attachAutomaticValidator(this.order, SubLedgerReportChargeOrderRequestValidationRules);
    attachAutomaticValidator(this.charge, SubLedgerReportChargeRequestValidationRules);
    attachAutomaticValidator(this.transfer, SubLedgerReportTransferPaymentRequestValidationRules);
    attachAutomaticValidator(this.card, SubLedgerReportPayGatePaymentRequestValidationRules);
    attachAutomaticValidator(this.summary, SubLedgerReportSummaryAccountingDocumentRequestValidationRules);
  }

  @computed
  get canGenerateReport(): boolean {
    return this.reportGenerationPossibility != SubLedgerReportPossible.NotPossible;
  }

  @action.bound
  @watchBusy
  async submit() {
    if (
      !validate(this.filter) ||
      !validate(this.order) ||
      !validate(this.charge) ||
      !validate(this.transfer) ||
      !validate(this.card) ||
      !validate(this.summary)
    ) {
      return;
    }

    const request = this.transformRequestForApi();
    let canSearch = false;
    const possibility = this.reportGenerationPossibility;

    let preview: SubLedgerReportPreviewResponse;

    switch (possibility) {
      case SubLedgerReportPossible.NotPossible:
        canSearch = false;
        break;
      case SubLedgerReportPossible.PreviewRequired:
        preview = await this.repository.preview(request);
        canSearch = preview.permissibility == AccountingDocumentSearchPermissibility.SearchPermitted;

        if (!canSearch) {
          this.previewFailedTooManyRows = preview.includedRows;
        }

        break;
      case SubLedgerReportPossible.Possible:
        canSearch = true;
        break;
    }

    if (!canSearch) {
      return;
    }

    try {
      await this.repository.createReport(request);
      this.showSuccessModal = true;
    } catch (e: any) {
      if (
        e.response.status === 400 &&
        [
          SilentErrors.SUB_LEDGER_REPORT_FILTER_CHARGE_ORDERS_NOT_FOUND,
          SilentErrors.SUB_LEDGER_REPORT_FILTER_CHARGES_NOT_FOUND,
          SilentErrors.SUB_LEDGER_REPORT_FILTER_TRANSFER_PAYMENTS_NOT_FOUND,
          SilentErrors.SUB_LEDGER_REPORT_FILTER_PAYGATE_PAYMENTS_NOT_FOUND,
          SilentErrors.SUB_LEDGER_REPORT_FILTER_SUMMARY_ACCOUNTING_DOCUMENTS_NOT_FOUND,
        ].includes(e.content?.type)
      ) {
        this.showNotFoundModal = e.content?.type;
      }
    }
  }

  @action.bound
  reset() {
    this.createRequest();
  }

  private dirtyObj(obj: any): boolean {
    return isFilterDirty(excludeEmptyValues(obj)) || !isFilterEmpty(excludeEmptyValues(obj));
  }

  private isBankAccountPresent(): boolean {
    return !!(this.transfer.bankAccount && (this.transfer.bankAccount.accountNumber || this.transfer.bankAccount.bank));
  }

  @computed get reportGenerationPossibility(): SubLedgerReportPossible {
    const filter = excludeEmptyValues(this.filter) as SubLedgerReportRequest;
    const order = excludeEmptyValues(this.order) as SubLedgerReportChargeOrderRequestX;
    const charge = excludeEmptyValues(this.charge) as SubLedgerReportChargeRequestX;
    const card = excludeEmptyValues(this.card) as SubLedgerReportPayGatePaymentRequest;
    const summary = excludeEmptyValues(this.summary) as SubLedgerReportSummaryAccountingDocumentRequest;

    if (filter.sequenceNumber && this.isDirtyExcept(this.filter)) {
      return SubLedgerReportPossible.Possible;
    }

    if (order.id || order.orderIdent || order.email || order.chargeId || order.licensePlate) {
      return SubLedgerReportPossible.Possible;
    }

    if (order.createdDate && (order.kioskId || order.salespointId)) {
      return SubLedgerReportPossible.Possible;
    }

    if (charge.id || charge.licensePlate || charge.email) {
      return SubLedgerReportPossible.Possible;
    }

    if (this.isTransferDirty) {
      return SubLedgerReportPossible.Possible;
    }

    if (card.cardNumber || card.authCode || card.arn || card.variableSymbol) {
      return SubLedgerReportPossible.Possible;
    }

    if (summary.sequenceNumber) {
      return SubLedgerReportPossible.Possible;
    }

    if (!this.isDirtyExcept(this.filter) && this.dirtyObj(this.filter)) {
      return SubLedgerReportPossible.PreviewRequired;
    }

    return SubLedgerReportPossible.NotPossible;
  }

  transformRequestForApi(): SubLedgerReportRequest {
    const filter = excludeEmptyValues(this.filter) as SubLedgerReportRequest;

    if (this.dirtyObj(this.order)) {
      filter.chargeOrder = excludeEmptyValues(this.order);
    } else if (this.dirtyObj(this.charge)) {
      filter.charge = excludeEmptyValues(this.charge);
    } else if (this.isTransferDirty) {
      filter.transferPayment = excludeEmptyValues(this.transfer);

      if (this.isBankAccountPresent()) {
        filter.transferPayment.bankAccount = excludeEmptyValues(this.transfer.bankAccount ?? {}) as any;
      } else {
        filter.transferPayment.bankAccount = undefined;
      }
    } else if (this.dirtyObj(this.card)) {
      filter.payGatePayment = excludeEmptyValues(this.card);
    } else if (this.dirtyObj(this.summary)) {
      filter.summaryAccountingDocument = excludeEmptyValues(this.summary) as any;
    }

    return filter;
  }

  @computed get isTransferDirty() {
    return (
      this.isBankAccountPresent() ||
      !!this.transfer.variableSymbol ||
      !!this.transfer.constantSymbol ||
      !!this.transfer.specificSymbol
    );
  }

  @computed get isDirty(): boolean {
    return (
      this.dirtyObj(this.filter) ||
      this.dirtyObj(this.order) ||
      this.dirtyObj(this.charge) ||
      this.isTransferDirty ||
      this.dirtyObj(this.card) ||
      this.dirtyObj(this.summary)
    );
  }

  private isDirtyExcept(obj: any) {
    for (const n of [this.charge, this.order, this.transfer, this.card, this.summary]) {
      if (obj == n) {
        continue;
      }

      if (n == this.transfer && !this.isTransferDirty) {
        continue;
      }

      if (this.dirtyObj(n)) {
        return true;
      }
    }
    return false;
  }

  @computed get canEditOrder(): boolean {
    return !this.isDirtyExcept(this.order);
  }

  @computed get canEditCharge(): boolean {
    return !this.isDirtyExcept(this.charge);
  }

  @computed get canEditTransfer(): boolean {
    return !this.isDirtyExcept(this.transfer);
  }

  @computed get canEditCard(): boolean {
    return !this.isDirtyExcept(this.card);
  }

  @computed get canEditSummary(): boolean {
    return !this.isDirtyExcept(this.summary);
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get accountingPeriodOptions() {
    return this.monthOptionService.monthOptions;
  }

  validateDateRange<T>(target: T, propertyName: PropertyName<T>) {
    const range = target[propertyName] as DateRangeRequest | undefined;

    removeError(target, propertyName);

    if (range) {
      attachManualValidator(target, true);

      if (!range.from || !range.to) {
        addError(target, propertyName, this.localizationService.translate("validators.customMessages.noneOrBothDates"));
      } else {
        const diffTime = Math.abs(range.to.getTime() - range.from.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 31) {
          addError(
            target,
            propertyName,
            this.localizationService.translate("validators.customMessages.intervalIsBiggerThan31Days")
          );
        }
      }
    }
  }

  async loadKiosks() {
    this.kiosks = (await this.kioskRepository.getAllFromSimpleList()).items.map(k => {
      return { id: k.id, label: k.name };
    });
  }

  @action.bound
  onSalesPointSearch(search: string) {
    if (search.length < 3) {
      return;
    }

    this.distributorRepository.salesPoints(search).then(response => {
      runInAction(() => {
        this.salespoints = response.items.map(s => {
          let label = `${s.name}`;
          if (s.address) {
            label += ` — ${s.address}`;
          }
          if (s.postalCode) {
            label += ` ${s.postalCode}`;
          }
          return { id: s.id, label };
        });
      });
    });
  }

  @action.bound
  closeSuccessModal() {
    scrollTop();
    this.showSuccessModal = false;
    this.eventBus.publish(subLedgerReportGenerated());
    this.onCloseClick?.();
  }

  @action.bound
  closeErrorModal() {
    this.previewFailedTooManyRows = undefined;
    this.showNotFoundModal = undefined;
  }

  translate(code: string) {
    return this.localizationService.translateModule(Modules.finance, `subLedger.${code}`);
  }

  translateAttribute(code: string, size?: number) {
    return this.localizationService.translate(`attributes.${code}`, size);
  }
}
